import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { captureError } from '@shared/lib/observability';
import { CampaignChat } from '@shared/ui/CampaignChat';
import { UnreadBadge } from '@shared/ui/UnreadBadge';
import { useUnreadThreads } from '@shared/hooks/useUnreadThreads';
import { useCreator } from './useCreator';

// Creator "My applications": campaigns this creator applied to (job-board model),
// with the current status. Reads get_my_applications(); withdraw via withdraw_application().

interface AppRow {
  application_id: string;
  status: 'applied' | 'shortlisted' | 'accepted' | 'rejected' | 'withdrawn';
  proposed_price: number | null;
  currency: string;
  applied_at: string;
  campaign_id: string;
  title: string;
  brand_name: string | null;
  campaign_status: string;
  compensation_type: string;
  budget: number | null;
  budget_basis: string;
}

const fmtMoney = (n: number | null, cur: string) => (n == null ? null : `${n.toLocaleString('ru-RU')} ${cur === 'KGS' ? 'сом' : cur}`);

export default function CreatorRequests() {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const { provider } = useCreator();
  const invalidate = useInvalidateSupabase();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [chatApp, setChatApp] = useState<AppRow | null>(null);
  const { byApplication } = useUnreadThreads();

  const q = useSupabaseQuery<AppRow[]>(
    ['creator-applications', provider?.id],
    async () => supabase.rpc('get_my_applications'),
    { enabled: !!provider?.id },
  );

  const withdraw = async (id: string) => {
    setBusy(id); setErr(null);
    const { error } = await supabase.rpc('withdraw_application', { p_application_id: id });
    setBusy(null);
    if (error) { captureError(error, { where: 'creator.applications.withdraw', id }); setErr(error.message); return; }
    invalidate(['creator-applications']);
  };

  const STATUS: Record<string, { label: string; color: string }> = {
    applied: { label: tr('creator.apps.applied', 'Отправлено'), color: '#6699ff' },
    shortlisted: { label: tr('creator.apps.shortlisted', 'В шорт-листе'), color: '#f5b700' },
    accepted: { label: tr('creator.apps.accepted', 'Принято'), color: '#8bd450' },
    rejected: { label: tr('creator.apps.rejected', 'Отклонено'), color: '#889' },
    withdrawn: { label: tr('creator.apps.withdrawn', 'Отозвано'), color: '#778' },
  };

  const rows = q.data ?? [];

  return (
    <div className="cr-page">
      <h2 style={{ color: 'var(--text)', fontSize: 18, margin: '4px 0 2px' }}>{tr('creator.apps.title', 'Мои отклики')}</h2>
      <p style={{ color: 'var(--dim)', fontSize: 12.5, margin: '0 0 12px' }}>
        {tr('creator.apps.sub', 'Задания, на которые вы откликнулись. Здесь виден статус — бренд свяжется с выбранными.')}
      </p>

      {err && <div style={{ color: '#e74c3c', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      {q.isLoading ? (
        <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13, padding: 20, textAlign: 'center' }}>
          {tr('creator.apps.empty', 'Вы ещё не откликались. Откройте «Спрос» и подайтесь на задание.')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((a) => {
            const st = STATUS[a.status] ?? { label: a.status, color: '#7a8' };
            const money = a.compensation_type === 'barter' ? tr('creator.apps.barter', 'Бартер') : fmtMoney(a.budget, a.currency);
            const canWithdraw = a.status === 'applied' || a.status === 'shortlisted';
            return (
              <div key={a.application_id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, color: st.color, background: `${st.color}22`, border: `1px solid ${st.color}55` }}>{st.label}</span>
                  {a.brand_name && <span style={{ fontSize: 12, color: 'var(--dim)' }}>{a.brand_name}</span>}
                </div>
                <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{a.title}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, color: 'var(--dim)', fontSize: 12 }}>
                  {money && <span style={{ color: 'var(--text-2)' }}>{money}</span>}
                  {a.proposed_price != null && <span>{tr('creator.apps.yourPrice', 'ваша цена')}: {a.proposed_price.toLocaleString('ru-RU')} {a.currency === 'KGS' ? 'сом' : a.currency}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => setChatApp(a)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(74,158,255,0.14)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {tr('creator.apps.chat', 'Чат с брендом')}
                    <UnreadBadge count={byApplication.get(a.application_id) ?? 0} />
                  </button>
                  {canWithdraw && (
                    <button onClick={() => withdraw(a.application_id)} disabled={busy === a.application_id}
                      style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: 'var(--muted)', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {tr('creator.apps.withdraw', 'Отозвать отклик')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {chatApp && (
        <CampaignChat
          applicationId={chatApp.application_id}
          title={chatApp.title}
          subtitle={chatApp.brand_name ?? tr('creator.apps.chatWithBrand', 'Чат с брендом')}
          onClose={() => setChatApp(null)}
        />
      )}
    </div>
  );
}
